import type React from 'react';
import { useEffect, useState } from 'react';
import Image from 'next/image';
import { Box } from '@mui/material';
import { isFalsy } from 'utility-types';
import Image2 from '@/images/carousel/guide/negative_review_need_action.png';
import Image7 from '@/images/carousel/guide/new_message.gif';
import Image5 from '@/images/carousel/guide/no_reply_review.png';
import Image1 from '@/images/carousel/guide/profile.gif';
import Image3 from '@/images/carousel/guide/remove_negative_review.png';
import Image4 from '@/images/carousel/guide/reply_to_negative_review.png';
import Image6 from '@/images/carousel/guide/reply_to_review.png';
import type { CarouselData } from '@/interfaces/component/carousel.interface';
import { getToken } from '@/utils/auth.util';
import FlexBox from '@/components/FlexBox';
import CustomMuiButton from '@/components/button/CustomMuiButton';
import Carousel from '@/components/carousel/Carousel';
import BaseModal from '@/components/modal/BaseModal';
import theme from '@/app/theme';
import usePollingGetReviewPosts from '@/hooks/usePollingGetReviewPosts';

const CarouselImageContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        padding: '15px',
        borderRadius: '8px',
        backgroundColor: theme.palette.gray[100]
      }}
    >
      {children}
    </Box>
  );
};

const carouselDataList: CarouselData[] = [
  {
    cardTitle: '정보 수집중',
    contents: <Image src={Image1.src} width={200} height={200} alt="정보 수집중" />,
    description: '최대 10분 정도 소요됩니다.\n기다리시는 동안 서비스 사용법을 공유드릴게요.'
  },
  {
    cardTitle: '대응 필요 부정 리뷰를 확인 하세요',
    contents: <Image src={Image2.src} width={320} height={198} alt="정보 수집중" />,
    description: '수집된 정보에 대해 긍정-부정 여부를 판별합니다.\n부정적인 리뷰는 삭제 또는 답변을 해주세요.'
  },
  {
    cardTitle: '부정 리뷰 삭제하기',
    contents: (
      <CarouselImageContainer>
        <Image src={Image3.src} width={248} height={289} alt="정보 수집중" />
      </CarouselImageContainer>
    ),
    description: '악성 리뷰에 삭제 버튼을 클릭해 주세요\n리뷰웍스에서 삭제 해드립니다.'
  },
  {
    cardTitle: '부정 리뷰 답변 하기',
    contents: (
      <CarouselImageContainer>
        <Image src={Image4.src} width={248} height={289} alt="정보 수집중" />
      </CarouselImageContainer>
    ),
    description: '껄끄러운 부정리뷰에도 AI로 쉽게 답변을 작성하세요\n다른 환자들이 봤을 때 병원의 신뢰를 가집니다.'
  },
  {
    cardTitle: '미답변 리뷰 답변하기',
    contents: <Image src={Image5.src} width={320} height={160} alt="정보 수집중" />,
    description: '환자 리뷰에 답변은 기존 환자에 대한 반응으로\n신규 환자로 하여금 좋은 인상을 줍니다.\n또한, 다른 환자들의 추가 리뷰를 유도합니다.'
  },
  {
    cardTitle: '미답변 리뷰 답변하기',
    contents: (
      <CarouselImageContainer>
        <Image src={Image6.src} width={200} height={264} alt="정보 수집중" />
      </CarouselImageContainer>
    ),
    description: '리뷰웍스 AI가 병원과 환자를 위한 최적의 답변을 생성합니다.'
  }
];

const NoPostDataOnCrawling = ({ isMobile, onCrawlingAppStoreId }: { isMobile: boolean; onCrawlingAppStoreId: number }) => {
  const [dataList, setDataList] = useState<CarouselData[]>();

  const { showCrawlingNoticeModal, crawlingNoticeModalProps, showGoToListPageButton, toggleShowCrawlingNoticeModal, gotoReviewsPage } = usePollingGetReviewPosts(onCrawlingAppStoreId);

  useEffect(() => {
    const token = getToken();

    let cardTitle = '회원가입하시면 알림을 드립니다';
    let description = '아직 수집중이군요...\n회원가입하시면 수집 완료 시 알려드려요.\n부정 리뷰가 생겨도 알려드려요.';

    if (!isFalsy(token)) {
      cardTitle = '수집이 완료되면 알림을 드립니다';
      description = '아직 수집중이군요...조금만 더 기다려 주세요.';
    }

    setDataList([
      ...carouselDataList,
      {
        cardTitle,
        contents: <Image src={Image7.src} width={200} height={200} alt="정보 수집중" />,
        description
      }
    ]);
  }, []);

  return dataList ? (
    <>
      <FlexBox
        style={{
          flex: 1,
          maxWidth: '340px',
          paddingY: isMobile ? 0 : '80px',
          gap: '24px',
          flexDirection: 'column'
        }}
      >
        <Carousel dataList={dataList} />
        {showGoToListPageButton ? <CustomMuiButton size="xl" type="PRIMARY" label="분석된 리뷰 확인 하기" fullWidth={isMobile} onClick={gotoReviewsPage} /> : ''}
      </FlexBox>
      {showCrawlingNoticeModal ? <BaseModal {...crawlingNoticeModalProps} showModal={showCrawlingNoticeModal} handleShowModal={toggleShowCrawlingNoticeModal} /> : ''}
    </>
  ) : (
    ''
  );
};

export default NoPostDataOnCrawling;
